import { useMemo, type FC } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { MODALS } from '@/context'
import { useModal, useTranslator } from '@/hooks'
import { LANG, MAIN_NAVIGATION_TRANSLATION } from '@/utils'
import type { TypeCmsLayout, TypeLang } from '@/schema'
import { defaultGrid, designGrid, innerGrid, mq } from '@/styles'
import { Img, Link } from '../commons'
import { LangDropdown } from './LangDropdown'

type Props = {
  menuLinks?: TypeCmsLayout['menu']
}

const SVG_LOGO_VADEVER = {
  alt: 'Vadever Logotype',
  type: 'svg' as const,
  src: '/images/svg/logo--vadever.svg',
  size: { width: 183, height: 33 },
  content_type: 'image/svg+xml'
}

const SVG_CONTACT_BUBBLES = {
  alt: 'Contact icon',
  type: 'svg' as const,
  src: '/images/svg/i--contact-bubbles.svg',
  size: { width: 28, height: 27 },
  content_type: 'image/svg+xml'
}

const getItems = (menuLinks: TypeCmsLayout['menu'], slug?: string) => menuLinks?.map((item) => {
  const isActive = slug === item.slug
  if(item.external) {
    return (
      <li key={item.slug}>
        <a href={item.slug} target='_blank' rel='noopener noreferrer'>{item.title}</a>
      </li>
    )
  }

  return (
    <li key={item.slug}>
      <Link href={`/${item.slug}`} {...isActive && { className: 'ac' }}>{item.title}</Link>
    </li>
  )
}) || []


const reelsLink = {
  external: true,
  title: 'REELS',
  slug: 'https://reels.vadever.com'
}
export const Navbar: FC<Props> = ({ menuLinks = [] }) => {
  const { lang, slug } = useParams() as { lang: TypeLang, slug?: string }
  const { toggle } = useModal(MODALS.MENU)
  const { toggle:toggleContact } = useModal(MODALS.CONTACT)
  const { t } = useTranslator()
  const Items = useMemo(() => getItems([...menuLinks, reelsLink], slug), [menuLinks, slug])

  const onClickMenu = () => {
    toggle()
  }

  const onClickContact = () => {
    toggleContact()
  }

  return (
    <Nav aria-label={MAIN_NAVIGATION_TRANSLATION[lang ?? LANG]}>
      <div>
        <button type='button' aria-haspopup='dialog' className='btn btn--menu' aria-controls={MODALS.MENU} aria-label='Menu' onClick={onClickMenu}>
          <svg aria-hidden='true' xmlns='http://www.w3.org/2000/svg' width='22' height='17' viewBox='0 0 22 17'><path d='M.68 1.58h21M.68 8.5h21m-21 6.92h21' stroke='var(--color-text)' strokeWidth='2.33' fill='none' strokeLinejoin='round' /></svg>
        </button>
        <ul className='ul--links'>
          {Items}
          {/* <li><a href='https://reels.vadever.com'>REELS</a></li> */}
        </ul>
        <Link href='/' className='logo'>
          <Img media={SVG_LOGO_VADEVER} />
        </Link>
        <LangDropdown />
        <button className='btn btn--contact' onClick={onClickContact}>
          <Img media={SVG_CONTACT_BUBBLES} />
          <span>{t('contact_us')}</span>
        </button>
      </div>
    </Nav>
  )
}

const Nav = styled.nav`
  background-color: var(--color-background);
  ${designGrid({})}
  position: sticky;
  top: 0;
  width: 100vw;
  width: 100dvw;
  z-index: 10;

  > div {
    align-items: center;
    display: flex;
    height: 75px;
    grid-column: 1 / span 6;
    justify-content: space-between;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
      justify-content: center;
      ${innerGrid(defaultGrid.tablet)}
    }

    ${mq.greaterThan('desktop')} {
      ${innerGrid(defaultGrid.desktop)}
    }
  }

  .dropdown--lang {
    grid-column: 10 / span 1;
    margin-left: auto;

    ${mq.lessThan('tablet')} {
      display: none;
    }
  }

  button {
    align-items: center;
    display: flex;
    justify-content: center;

    &.btn--menu {
      ${mq.greaterThan('desktop-md')} {
        display: none;
      }
    }

    &.btn--contact {
      align-items: center;
      background-color: var(--color-gold);
      border-radius: 100px;
      border: 2px solid var(--color-gold);
      display: flex;
      justify-content: center;
      padding: 14px;
      transition: border-color 250ms ease-in-out;
      width: fit-content;

      ${mq.greaterThan('tablet')} {
        flex-direction: column;
        grid-column: 11 / span 2;
        margin-left: auto;
        padding: 20px;
      }

      &:hover {
        border-color: var(--color-black);
      }

      span {
        font-size: 15px;
        font-weight: 500;

        ${mq.lessThan('tablet')} {
          display: none;
        }
      }

      img {
        width: 26px;

        ${mq.greaterThan('tablet')} {
          display: none;
        }
      }
    }
  }

  a {
    align-items: center;
    display: flex;
    justify-content: center;

    &.logo {
      transform: translateY(-2px);
      width: 142px;

      ${mq.greaterThan('tablet')} {
        grid-column: 6 / span 2;
        width: 182px;
      }

      img {
        width: 100%;
      }
    }
  }

  .ul--links {
    display: none;

    ${mq.greaterThan('desktop-md')} {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: 15px;
      justify-content: flex-start;
      list-style: none;
      transition: 250ms gap linear;
    }

    ${mq.greaterThan('desktop')} {
      gap: 16px;
    }

    ${mq.greaterThan('desktop-wide')} {
      gap: 20px;
    }

    ${mq.greaterThan('desktop-large')} {
      gap: 30px;
    }

    a {
      opacity: .4;
      text-transform: uppercase;
      transition: opacity 250ms ease-in-out;
      letter-spacing: -.1px;

      ${mq.greaterThan('desktop-wide')} {
        letter-spacing: 0;
      }

      &.ac {
        opacity: 1;
      }

      &:hover {
        opacity: .8;
      }
    }
  }
`
